// VoiceListeningOverlay — fullscreen frame that signals voice state.
//
//   "idle"      → invisible
//   "listening" → bloom + spinning ring (purple/pink). Continuous
//                 wake+command flows hit listening for ~100ms via
//                 handleFire's enterListening-then-enterFiring
//                 pattern, so the user always sees the wake-heard
//                 cue before the firing flash.
//   "firing"    → bloom + ring fade out IMMEDIATELY; the green
//                 flash takes the screen alone. Two overlays at
//                 once read as a glitchy purple/green smear, so
//                 we hand off cleanly: listening = purple, firing
//                 = green, never both at the same time.
//
// Pointer-events: none on every layer.

const STYLES = `
  @property --voice-angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
  @keyframes voice-spin {
    to { --voice-angle: 360deg; }
  }
  @keyframes voice-fire-flash {
    0%   { opacity: 0; }
    18%  { opacity: 1; }
    100% { opacity: 0; }
  }

  .voice-overlay {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 9999;
    opacity: 0;
    /* Asymmetric fade: bloom in is leisurely (380ms ease-out so the
       ring's shrink-in feels intentional); bloom out is sharp (180ms)
       so when state flips listening → firing, the purple cleanly
       hands off to the green flash without a smeared overlap. */
    transition: opacity 380ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  .voice-overlay.is-listening {
    opacity: 1;
  }
  .voice-overlay:not(.is-listening) {
    transition: opacity 180ms ease-in;
  }

  .voice-overlay-bloom {
    position: absolute;
    inset: 0;
    border-radius: 50px;
    /* Bloom ~15% tighter than the green firing flash. */
    background: radial-gradient(
      ellipse at center,
      rgba(155, 48, 255, 0.0) 51%,
      rgba(155, 48, 255, 0.18) 82%,
      rgba(255, 20, 147, 0.32) 100%
    );
    box-shadow:
      inset 0 0 55px 7px rgba(255, 20, 147, 0.55),
      inset 0 0 32px 2px rgba(155, 48, 255, 0.45),
      inset 0 0 15px 0 rgba(40, 40, 255, 0.32);
  }

  .voice-overlay-ring {
    position: absolute;
    inset: 0;
    /* iPhone screen-corner radius (Pro lineup ~55, standard ~47);
       50 splits the difference and looks tight on every device. */
    border-radius: 50px;
    background: conic-gradient(
      from var(--voice-angle, 0deg),
      #FF1493 0deg,
      #9B30FF 90deg,
      #1A1AFF 180deg,
      #9B30FF 270deg,
      #FF1493 360deg
    );
    /* 10px ring; small blur softens the inner edge so it bleeds
       into the bloom instead of meeting it as a hard seam. */
    padding: 10px;
    -webkit-mask:
      linear-gradient(#000, #000) content-box,
      linear-gradient(#000, #000);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    filter: blur(2px);
    animation: voice-spin 1.6s linear infinite;
    /* Shrink-in entrance: ring starts at 120% (extending well past
       the screen edge, mostly clipped) and constricts to 100% with
       a strong ease-out — rushes in fast, slows exponentially as it
       lands on its resting border. */
    transform: scale(1.20);
    transition: transform 700ms cubic-bezier(0.16, 1, 0.3, 1);
  }
  .voice-overlay.is-listening .voice-overlay-ring { transform: scale(1); }

  .voice-overlay-fire {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 10000;
    opacity: 0;
    background: radial-gradient(
      ellipse at center,
      rgba(60, 200, 110, 0.0) 35%,
      rgba(60, 200, 110, 0.32) 100%
    );
    box-shadow: inset 0 0 80px 6px rgba(60, 200, 110, 0.55);
    border-radius: 50px;
  }
  .voice-overlay-fire.is-firing {
    animation: voice-fire-flash 700ms ease-out;
  }
`;

export default function VoiceListeningOverlay({ state }) {
  const isListening = state === "listening";
  const isFiring = state === "firing";
  return (
    <>
      <style>{STYLES}</style>
      <div className={`voice-overlay${isListening ? " is-listening" : ""}`}>
        <div className="voice-overlay-bloom" />
        <div className="voice-overlay-ring" />
      </div>
      <div className={`voice-overlay-fire${isFiring ? " is-firing" : ""}`} />
    </>
  );
}
